import { useState } from 'react'
import { Navigate, useNavigate, useOutletContext } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import type { GccEligibilityRule, GccPayRateEntry } from '@shared/types'
import type { ClientBranding } from '@shared/types'
import { DEFAULT_ELIGIBILITY_RULES, GCC_DEFAULT_AUTOMATE_FIRST_THRESHOLD_PCT, GCC_DEFAULT_QUICK_WIN_HORIZON_MONTHS } from '@tn/shared'
import { deriveBrandingVariants } from '../index'
import GccStep1_ClientDetails from './GccStep1_ClientDetails'
import GccStep2_Config from './GccStep2_Config'
import GccStep_Generate from './GccStep_Generate'

export interface GccWizardFormData {
  clientName: string
  industry: string
  functionScope: string[]
  uploadedFiles: Array<{ base64: string; name: string }>
  eligibilityRules: GccEligibilityRule[]
  payRateTable: GccPayRateEntry[]
  /** Whole % of a task's work AI must release to keep it from migrating (WI 2048) */
  automateFirstThresholdPct: number
  /** Whole months a low-complexity Automate / Agentify task must be live within to be a quick win (WI 2806) */
  quickWinHorizonMonths: number
  primaryColor: string
  branding: ClientBranding
}

const STEP_LABELS = ['Client Details', 'GCC Config', 'Generate']

export default function GccWizard() {
  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [projectId] = useState(() => uuidv4())
  const [clientName, setClientName] = useState('')
  const [industry, setIndustry] = useState('')
  const [functionScope, setFunctionScope] = useState<string[]>([])
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ base64: string; name: string }>>([])
  const [eligibilityRules, setEligibilityRules] = useState<GccEligibilityRule[]>(DEFAULT_ELIGIBILITY_RULES)
  const [payRateTable] = useState<GccPayRateEntry[]>([])
  const [automateFirstThresholdPct, setAutomateFirstThresholdPct] = useState(GCC_DEFAULT_AUTOMATE_FIRST_THRESHOLD_PCT)
  const [quickWinHorizonMonths, setQuickWinHorizonMonths] = useState(GCC_DEFAULT_QUICK_WIN_HORIZON_MONTHS)
  const [primaryColor, setPrimaryColor] = useState('#059669')
  const navigate = useNavigate()
  const { refreshProjects, isDemoActive = false } = useOutletContext<{ refreshProjects: () => Promise<void>; isDemoActive?: boolean }>()

  const variants = deriveBrandingVariants(primaryColor)
  const branding: ClientBranding = {
    clientName,
    primaryColor,
    primaryColorDark: variants.primaryColorDark,
    primaryColorLight: variants.primaryColorLight,
    isWip: true,
  }

  const formData: GccWizardFormData = {
    clientName, industry, functionScope, uploadedFiles,
    eligibilityRules, payRateTable, automateFirstThresholdPct, quickWinHorizonMonths, primaryColor, branding,
  }

  // WI 2368: no new GCC designs in demo mode, even via a direct /gcc/wizard link.
  if (isDemoActive) return <Navigate to="/gcc" replace />

  return (
    <div className="min-h-full flex flex-col">
      {/* Wizard step indicator */}
      <div className="bg-white border-b border-gray-100 px-8 py-5">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center">
            {STEP_LABELS.map((label, i) => {
              const n = (i + 1) as 1 | 2 | 3
              const isActive = n === step
              const isDone = n < step
              return (
                <div key={n} className="flex items-center flex-1">
                  <div className={`flex items-center gap-1.5 ${isActive ? 'text-emerald-700' : isDone ? 'text-emerald-600' : 'text-gray-400'}`}>
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 flex-shrink-0 transition-colors ${
                        isActive
                          ? 'border-emerald-600 bg-emerald-600 text-white'
                          : isDone
                          ? 'border-emerald-500 bg-emerald-500 text-white'
                          : 'border-gray-200 bg-white text-gray-400'
                      }`}
                    >
                      {isDone ? (
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : n}
                    </div>
                    <span className="text-sm font-medium whitespace-nowrap">{label}</span>
                  </div>
                  {i < STEP_LABELS.length - 1 && (
                    <div className={`flex-1 h-px mx-3 transition-colors ${isDone ? 'bg-emerald-400' : 'bg-gray-200'}`} />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Step content */}
      <div className="flex-1">
        {step === 1 && (
          <GccStep1_ClientDetails
            clientName={clientName} setClientName={setClientName}
            industry={industry} setIndustry={setIndustry}
            uploadedFiles={uploadedFiles} setUploadedFiles={setUploadedFiles}
            primaryColor={primaryColor} setPrimaryColor={setPrimaryColor}
            onBack={() => navigate('/gcc')}
            onNext={() => setStep(2)}
          />
        )}
        {step === 2 && (
          <GccStep2_Config
            functionScope={functionScope} setFunctionScope={setFunctionScope}
            eligibilityRules={eligibilityRules} setEligibilityRules={setEligibilityRules}
            automateFirstThresholdPct={automateFirstThresholdPct} setAutomateFirstThresholdPct={setAutomateFirstThresholdPct}
            quickWinHorizonMonths={quickWinHorizonMonths} setQuickWinHorizonMonths={setQuickWinHorizonMonths}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
          />
        )}
        {step === 3 && (
          <GccStep_Generate
            projectId={projectId}
            formData={formData}
            onBack={() => setStep(2)}
            onComplete={() => {
              void refreshProjects()
              navigate(`/gcc/dashboard/${projectId}`)
            }}
            onProjectDiscarded={() => void refreshProjects()}
          />
        )}
      </div>
    </div>
  )
}
